import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, X, Upload, Camera } from "lucide-react";
import { toast } from "sonner";

const PHOTO_TYPES = ["image/jpeg", "image/png", "image/heic", "image/webp"];
const PHOTO_EXT = [".jpg", ".jpeg", ".png", ".heic", ".webp"];
const DOC_TYPES = ["Insurance", "Business License", "Trade License", "W-9", "Identity Verification", "Trade Certification", "Other"];
const MAX = 25 * 1024 * 1024;

export function UploadPhotosDialog({ payout, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const add = (list) => {
    const next = [];
    for (const f of list) {
      if (!PHOTO_TYPES.includes(f.type) && !PHOTO_EXT.some((e) => f.name.toLowerCase().endsWith(e))) { toast.error(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX) { toast.error(`${f.name}: too large (max 25MB)`); continue; }
      next.push({ file: f, url: URL.createObjectURL(f), key: `${f.name}-${f.size}-${f.lastModified}` });
    }
    setFiles((prev) => { const seen = new Set(prev.map((p) => p.key)); return [...prev, ...next.filter((n) => !seen.has(n.key))]; });
  };
  const remove = (key) => setFiles((prev) => prev.filter((p) => p.key !== key));

  const submit = async () => {
    if (!files.length) return toast.error("Add at least one photo");
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f.file);
      fd.append("work_order_id", payout.work_order_id);
      fd.append("phase", "after");
      fd.append("client_ref", f.key);
      try { await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (e) { toast.error(e.response?.data?.detail || `Failed to upload ${f.file.name}`); }
    }
    setBusy(false);
    if (ok) { toast.success(`After photo${ok > 1 ? "s" : ""} uploaded successfully. Eligibility updated.`); onDone(); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="upload-photos-modal">
        <DialogHeader><DialogTitle>Upload After Photos — {payout.wo_number}</DialogTitle><DialogDescription>JPG, PNG, HEIC or WEBP. Max 25MB each.</DialogDescription></DialogHeader>
        <input ref={inputRef} type="file" accept="image/*,.heic,.webp" capture="environment" multiple hidden data-testid="photo-file-input" onChange={(e) => { add(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <button data-testid="photo-picker-btn" onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors w-full">
          <Camera className="w-8 h-8 mx-auto mb-2" /><div className="text-sm font-medium">Tap to take a photo or choose files</div>
        </button>
        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="photo-preview-grid">
            {files.map((f) => (
              <div key={f.key} className="relative group">
                <img src={f.url} alt="" className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                <button data-testid={`remove-photo-${f.key}`} onClick={() => remove(f.key)} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-0.5 shadow"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="submit-photos-btn" disabled={busy || !files.length} onClick={submit} className="bg-blue-600 hover:bg-blue-700">{busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4 mr-1" />Upload {files.length || ""} Photo{files.length !== 1 ? "s" : ""}</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UploadDocDialog({ payout, onClose, onDone }) {
  const [docType, setDocType] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [exp, setExp] = useState("");
  const [num, setNum] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const pick = (f) => {
    if (!f) return;
    const okExt = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".webp"].some((e) => f.name.toLowerCase().endsWith(e));
    if (!okExt) return toast.error("Unsupported type. Use PDF, JPG, PNG, HEIC or WEBP.");
    if (f.size > MAX) return toast.error("File too large (max 25MB).");
    setFile(f);
    setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };

  const submit = async () => {
    if (!docType) return toast.error("Select a document type");
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    if (exp) fd.append("expiration_date", exp);
    if (num) fd.append("doc_number", num);
    if (notes) fd.append("notes", notes);
    try { await api.post("/upload/document", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document submitted for review."); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="upload-doc-modal">
        <DialogHeader><DialogTitle>Upload Required Document{payout ? ` — ${payout.wo_number}` : ""}</DialogTitle><DialogDescription>PDF, JPG, PNG, HEIC or WEBP. Max 25MB.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Document Type</Label><Select value={docType} onValueChange={setDocType}><SelectTrigger data-testid="doc-type-select" className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <input ref={inputRef} type="file" accept=".pdf,image/*,.heic,.webp" hidden data-testid="doc-file-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="doc-picker-btn" onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center text-slate-500 hover:border-blue-400 hover:text-blue-600 w-full transition-colors">
            <Upload className="w-6 h-6 mx-auto mb-1" /><div className="text-sm">{file ? file.name : "Tap to choose a file"}</div>
          </button>
          {preview && <img src={preview} alt="" className="w-full h-40 object-contain rounded-lg border border-slate-200 bg-slate-50" />}
          {file && <button data-testid="remove-doc-btn" onClick={() => { setFile(null); setPreview(null); }} className="text-rose-600 text-xs underline">Remove file</button>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Expiration (optional)</Label><Input data-testid="doc-exp" type="date" value={exp} onChange={(e) => setExp(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Document # (optional)</Label><Input data-testid="doc-num" value={num} onChange={(e) => setNum(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea data-testid="doc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="submit-doc-btn" disabled={busy} onClick={submit} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for Review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
